import React, { useEffect, useState } from "react";
import { Button, toastEmitter, Input, Icon } from "@anchor/react-components/dist/lib/components";
import { getAPI, postAPI } from "../../Services/APIservices";
import { SegmentListModel } from "./DictionaryModel";
import "./Dictionary.scss";

interface EditValues {
  [key: number]: { segmentUp: number; segmentDown: number };
}

const SegmentInput: React.FC<{
  id: number;
  label: string;
  value: number;
  icon: "arrow-up" | "arrow-down";
  onChange: (id: number, value: number) => void;
}> = ({ id, label, value, icon, onChange }) => {
  const isInvalid = value < 0 || value > 20;

  return (
    <div className="segment-input">
      <label>{label}</label>
      <div className="input-with-prefix">
        <Input
          icon={icon}
          type="number"
          min={0}
          max={20}
          value={value.toString()}
          onChange={(e) => onChange(id, Number(e.target.value))}
          fit="large"
          placeholder={label} id={""}        />
      </div>
      {isInvalid && <div className="segment-error">Values must be between 0 and 20</div>}
    </div>
  );
};

const SegmentsDictionary: React.FC = () => {
  const [segmentList, setSegmentList] = useState<SegmentListModel[]>([]);
  const [editValues, setEditValues] = useState<EditValues>({});

  const getSegmentMapping = async () => {
    try {
      const response = await getAPI("dtco-api/dictionary/GetSegmentMapping");
      const data = Array.isArray(response) ? response : [response];
      setSegmentList(data);

      const initialEditValues: EditValues = {};
      data.forEach((item) => {
        initialEditValues[item.id!] = { segmentUp: item.segmentUp, segmentDown: item.segmentDown };
      });
      setEditValues(initialEditValues);
    } catch (error) {
      console.error(error);
      toastEmitter({  className: "customClassName", title: "Error fetching segment data" }, { type: "error" });
    }
  };

  useEffect(() => {
    getSegmentMapping();
  }, []);

  const handleInputChange = (id: number, field: "segmentUp" | "segmentDown", value: number) => {
    setEditValues((prev) => ({
      ...prev,
      [id]: { ...prev[id], [field]: value },
    }));
  };

  const handleSave = async (id: number) => {
    const { segmentUp, segmentDown } = editValues[id];
    if ([segmentUp, segmentDown].some((v) => v < 0 || v > 20)) {
      return;
    }

    try {
      await postAPI("dtco-api/dictionary/UpsertSegmentMapping", { id, segmentUp, segmentDown });
      toastEmitter({ className: "customClassName", title: "Segment updated successfully" }, { type: "success" });
      getSegmentMapping();
    } catch (error) {
      console.error(error);
      toastEmitter({ className: "customClassName", title: "Error updating segment" }, { type: "error" });
    }
  };

  return (
    <div className="dictionary-page mt-3 p-4">
      <h1 className="header-text">Segment Mapping</h1>
      <div className="segment-form mt-4">
        {segmentList.map((segment) => {
          const values = editValues[segment.id!] || { segmentUp: 0, segmentDown: 0 };
          return (
            <form key={segment.id} className="segment-panel p-3 mb-3">
              <p className="segment-info">
                <Icon name="info-circle-solid" />
                This segment is used for mapping in the simulation model
              </p>
              <div className="segment-inputs-row">
                <SegmentInput
                  id={segment.id!}
                  label="Segment Up"
                  icon="arrow-up"
                  value={values.segmentUp}
                  onChange={(id, value) => handleInputChange(id, "segmentUp", value)}
                />
                <SegmentInput
                  id={segment.id!}
                  label="Segment Down"
                  icon="arrow-down"
                  value={values.segmentDown}
                  onChange={(id, value) => handleInputChange(id, "segmentDown", value)}
                />
                <div className="segment-save-button">
                  <Button
                    variant="primary"
                    fit="large"
                    label="Save"
                    onClick={() => handleSave(segment.id!)}
                    className="large-button"
                  />
                </div>
              </div>
            </form>
          );
        })}
      </div>
    </div>
  );
};

export default SegmentsDictionary;
